import { useCallback } from "react";
import useAppNavigation from "./use-app-navigation";
import { useSelector } from "react-redux";
import IRootState from "system/types/store/IRootState";
import { encodeUriString, statusToString } from "system/libraries/utilities";
import { SiteInfo } from "system/types/wireTypes";
import useUrlSearch from "./use-url-search";

const usePageNavigation = () => {
  const navigate = useAppNavigation();
  const { tenants, currentTenantId, defaultTenant } = useSelector((state: IRootState) => state.config);
  const { get: getSearchParam } = useUrlSearch();

  // The chart range lives in the url, so every hop has to carry it or the timespan is lost.
  const chartRangeSearch = useCallback((): string => {
    const rangeFrom = getSearchParam("rangeFrom");
    const rangeTo = getSearchParam("rangeTo");
    if (!rangeFrom || !rangeTo) {
      return "";
    }

    const params = new URLSearchParams();
    params.append("rangeFrom", rangeFrom);
    params.append("rangeTo", rangeTo);
    return params.toString();
  }, [getSearchParam]);

  // Shared so the breadcrumbs cannot drop the range again.
  const getHomePageUrl = useCallback(
    (selectedDate: string): string => {
      const params = new URLSearchParams({ selectedDate });
      const rangeSearch = chartRangeSearch();
      return `/?${[params.toString(), rangeSearch].filter(Boolean).join("&")}`;
    },
    [chartRangeSearch]
  );

  const navigateToHomePage = useCallback(() => {
    if (tenants?.find((x: SiteInfo) => x.siteId === currentTenantId)) {
      navigate(`/${currentTenantId}`, undefined, true);
    } else if (defaultTenant) {
      navigate(`/${defaultTenant}`, undefined, true);
    } else {
      navigate("/", undefined, true);
    }

    document.getElementById("root")?.scrollTo({
      top: 0,
      left: 0,
    });
  }, [tenants, currentTenantId, defaultTenant, navigate]);

  const getPackagesPageUrl = useCallback(() => {
    return "/packages";
  }, []);

  const getDocumentsPageUrl = useCallback(() => {
    return "/documents";
  }, []);

  const navigateToPackagesPage = useCallback(() => {
    const url = getPackagesPageUrl();
    if (url === null) {
      return;
    }

    navigate(url);
  }, [getPackagesPageUrl, navigate]);

  const navigateToDocumentsPage = useCallback(() => {
    const url = getDocumentsPageUrl();
    if (url === null) {
      return;
    }

    navigate(url);
  }, [getDocumentsPageUrl, navigate]);

  const getEndpointPageUrl = useCallback(
    (machineId?: number | string | null, atTime?: string | null, searchString?: string | null): string | null => {
      if (!machineId || isNaN(+machineId) || +machineId < 1) {
        return null;
      }

      const urlParams = new URLSearchParams();

      if (atTime) {
        urlParams.append("selectedDate", atTime);
      }

      if (searchString) {
        urlParams.append("search", searchString);
      }

      const rangeSearch = chartRangeSearch();
      const queryString = [urlParams.toString(), rangeSearch].filter(Boolean).join("&");
      return `/endpoint/${machineId}${queryString ? `?${queryString}` : ""}`;
    },
    [chartRangeSearch]
  );

  const navigateToEndpointPage = useCallback(
    (machineId?: number | string | null, atTime?: string | null, searchString?: string | null) => {
      const url = getEndpointPageUrl(machineId, atTime, searchString);
      if (url === null) {
        return;
      }

      navigate(url);
    },
    [navigate, getEndpointPageUrl]
  );

  const getEndpointReportHistoryPageUrl = useCallback((machineId?: number | string | null): string | null => {
    if (!machineId || isNaN(+machineId) || +machineId < 1) {
      return null;
    }

    return `/endpoint/${machineId}/history`;
  }, []);

  const navigateToEndpointReportHistoryPage = useCallback(
    (machineId?: number | string | null) => {
      const url = getEndpointReportHistoryPageUrl(machineId);
      if (url === null) {
        return;
      }

      navigate(url);
    },
    [navigate, getEndpointReportHistoryPageUrl]
  );

  const navigateToEndpointByStatusPage = useCallback(
    (status?: number | null, selectedDate?: string | null, searchString?: string | null) => {
      if (typeof status !== "number" || !selectedDate) {
        return;
      }

      const queryString = [searchString, chartRangeSearch()].filter(Boolean).join("&");
      navigate(`/endpointsbystatus/${statusToString(status)}/${selectedDate}` + (queryString ? `?${queryString}` : ""));
    },
    [navigate, chartRangeSearch]
  );

  const navigateToEndpointSubjectHistoryPage = useCallback(
    (machineId?: number | string | null, time?: string | null, subject?: string | null) => {
      if (!machineId || !time || !subject) {
        return;
      }

      navigate(`/endpoint/${machineId}/${time}/${subject}`);
    },
    [navigate]
  );

  const getSubjectByReasonPageUrl = useCallback(
    (subject?: string | null, selectedDate?: string | null, searchString?: string | null): string | null => {
      if (!subject || !selectedDate) {
        return null;
      }

      const rangeSearch = chartRangeSearch();

      return (
        `/subjectbyreason/${subject}?selectedDate=${selectedDate}` +
        (searchString ? `&${searchString}` : "") +
        (rangeSearch ? `&${rangeSearch}` : "")
      );
    },
    [chartRangeSearch]
  );

  const navigateToSubjectByReasonPage = useCallback(
    (subject?: string | null, selectedDate?: string | null, searchString?: string | null) => {
      const url = getSubjectByReasonPageUrl(subject, selectedDate, searchString);
      if (!url) {
        return;
      }

      navigate(url);
    },
    [navigate, getSubjectByReasonPageUrl]
  );

  const getEndpointsByReasonPageUrl = useCallback(
    (
      status?: number | null,
      subject?: string | null,
      selectedDate?: string | null,
      searchString?: string | null,
      message?: string | null
    ): string | null => {
      if (!subject || !selectedDate) {
        return null;
      }

      const queryString = [searchString, chartRangeSearch()].filter(Boolean).join("&");

      return (
        `/endpointsbyreason/${typeof status === "number" ? statusToString(status) : "all"}/${subject}/${selectedDate}` +
        (message ? `/${encodeUriString(message)}` : "") +
        (queryString ? `?${queryString}` : "")
      );
    },
    [chartRangeSearch]
  );

  const navigateToEndpointsByReason = useCallback(
    (
      status?: number | null,
      subject?: string | null,
      selectedDate?: string | null,
      searchString?: string | null,
      message?: string | null
    ) => {
      const url = getEndpointsByReasonPageUrl(status, subject, selectedDate, searchString, message);
      if (!url) {
        return;
      }

      navigate(url);
    },
    [navigate, getEndpointsByReasonPageUrl]
  );

  return {
    navigateToHomePage,
    getHomePageUrl,
    getEndpointPageUrl,
    navigateToEndpointPage,
    getEndpointReportHistoryPageUrl,
    navigateToEndpointReportHistoryPage,
    navigateToEndpointSubjectHistoryPage,
    navigateToEndpointByStatusPage,
    getSubjectByReasonPageUrl,
    navigateToSubjectByReasonPage,
    getEndpointsByReasonPageUrl,
    navigateToEndpointsByReason,
    getPackagesPageUrl,
    navigateToPackagesPage,
    navigateToDocumentsPage,
  };
};

export default usePageNavigation;
